// The hull hit recipes, mockup round 052 (R1007).
//
// One entry per look id of `labels.toml hit_hull_styles`, keyed by the id. The
// layer picks this list once the victim's shields are down (R1009). The rules
// of the file are the rules of `combat-recipes-melee.ts`.
//
// **A hull mark lands ON THE PLATE.** `onHull` pulls the arrival point to
// within `ONHULL_PX` of the hull centre, however wide the salvo spread was, and
// every card of round 052 reads that point. A card that wants the hull's own
// ink reads `c.hull.ink`, and it falls back to the arrival point and the
// shot's bearing when the viewer cannot see the hull.
//
// **THE KIT HAS NO CLIP, AND FOUR CARDS CLIPPED.** The mockup harness is
// canvas-2D, so a card could mask a gradient or a jagged run to the silhouette.
// The three batches cannot. Each of the four takes the nearest honest
// translation, and the recipe says which:
//
//   - LIST (14) redraws the whole hull, so it OWNS it: `OWNS_HULL` names it and
//     the layer takes the fleet layer's instance away. That is the map's answer
//     to `HK.erase`, the rule `combat-recipes-sink.ts` states.
//   - OUTLINE FLICKER (16) strokes the silhouette over the live hull, which is
//     what the card does, so it owns nothing.
//   - INNER FIRE (18) keeps the card's gradient, its radius and its alpha, and
//     loses the mask: the glow spills a few px past the plate.
//   - SHORT CIRCUIT (19) cuts each arc at the hull's own ellipse in place of
//     the mask, so an arc still reads as a run over the plate.
//
// **ONE SALVO IS SEVERAL MARKS.** A HIT event raises one mark per round of the
// salvo, and a look that redraws the whole hull would stack it. The LAYER
// names the newest mark of each victim and hands it `c.lead` (R1011); the rest
// draw their own strike alone. LIST, OUTLINE FLICKER and the silhouette flash
// of SHORT CIRCUIT ask.

import { ACCENT_COLOR, DETONATION_LIMITS } from '../gen/presentation';
import {
  TAU, COMBAT_WHITE, DEEP_INK, FOAM_INK, NEUTRAL_INK, PALE_INK,
  angOf, backOf, clamp01, disc, flare, fog, hash, hull, hump,
  inn, line, onHull, out, plate, sideOf, tail, zig,
  type HitRecipeFn, type HullMark, type Pt,
} from './combat-recipe-kit';

// ---------------------------------------------------------------------------
// The two inks of §2 a hull hit alone wears, beside the four of the kit
// ---------------------------------------------------------------------------

/** The accent of the chrome: the one electric ink of the round. */
const ACCENT_INK = ACCENT_COLOR;
/** Fire, the one hot ink. */
const FIRE_INK = DETONATION_LIMITS.fire;

/**
 * 1 while the mark lives, 0 past it.
 *
 * A card that holds one pip or one silhouette at a flat alpha for its whole
 * life needs it: the mockup harness only ever ran a card from 0 to 1, and a
 * recipe must write nothing at all once the layer's own clock has passed the
 * life it states.
 */
function alive(p: number): number {
  return p < 1 ? 1 : 0;
}

/**
 * How many steps of a jagged run out of `(sx, sy)` still stand on the plate.
 *
 * `HK.zig` of card 19 is clipped to the silhouette; the kit has no clip, so the
 * run stops at the hull's OWN ELLIPSE instead — the ellipse the hull's half
 * length and half beam draw. The strike point is inside it, so the answer is
 * the positive root of the ray against that ellipse, in the hull's own frame.
 */
function plateSteps(
  h: HullMark, sx: number, sy: number, ang: number, step: number, max: number,
): number {
  const cos = Math.cos(h.head), sin = Math.sin(h.head);
  const rx = sx - h.x, ry = sy - h.y;
  const px = rx * cos + ry * sin;
  const py = -rx * sin + ry * cos;
  const th = ang - h.head;
  const dx = Math.cos(th), dy = Math.sin(th);
  const a2 = Math.max(h.halfLenPx, 1) ** 2, b2 = Math.max(h.halfBeamPx, 1) ** 2;
  const qa = (dx * dx) / a2 + (dy * dy) / b2;
  const qb = 2 * ((px * dx) / a2 + (py * dy) / b2);
  const qc = (px * px) / a2 + (py * py) / b2 - 1;
  const root = qb * qb - 4 * qa * qc;
  if (qa <= 0 || root <= 0) return 1;
  const n = Math.round(((-qb + Math.sqrt(root)) / (2 * qa)) / step);
  return n < 1 ? 1 : n > max ? max : n;
}

// ---------------------------------------------------------------------------
// The proportions
// ---------------------------------------------------------------------------

/** fragment-fan, card 04: how wide the fan opens, in radians. */
const FAN_SPREAD = 1.5;
/** fragment-fan: the smallest plate, as a share of the widest. The card
 *  writes `1.6 + 2 * h2`, so a plate runs 1.6 to 3.6 world px. */
const FAN_PLATE_MIN = 1.6, FAN_PLATE_SPAN = 2;
/** fragment-fan: how fast a plate spins, in revolutions over the life. */
const FAN_SPIN = 3;
/** fragment-fan: the thinnest a plate turns to, and the span above it. */
const FAN_THIN_MIN = 0.25, FAN_THIN_SPAN = 0.75;
/** fragment-fan: how fast the thinning turns, as a multiple of the life. */
const FAN_THIN_RATE = 9;
/** fragment-fan: where the plates start to fade, as a share of the life. */
const FAN_FADE_AT = 0.5;
/** fragment-fan: how long the breach flash lives, as a share of the life. */
const FAN_FLASH_SPAN = 0.25;

/** spark-shower, card 02: how wide the cone opens back at the shooter. */
const SHOWER_SPREAD = 2;
/** spark-shower: how much of its reach a spark keeps as it is dragged down. */
const SHOWER_DRAG = 0.7;
/** spark-shower: how long a spark stays white and the breach flash lives, as
 *  a share of the life. The card writes one figure for both. */
const SHOWER_HOT_AT = 0.3;
/** spark-shower: where the hot pip cools to the deep ink. */
const SHOWER_PIP_COOL = 0.5;
/** spark-shower: how far a spark's tail droops, in world px. */
const SHOWER_DROOP = 1;

/** smoke-puffs, card 06: the gap between two puffs and the life of one, as
 *  shares of the life. */
const PUFF_STEP = 0.11, PUFF_SPAN = 0.5;
/** smoke-puffs: how far a puff swells over its own life, as a share of its
 *  radius at birth. The card writes `3 + 9 * age` and `2.5 + 6 * age`. */
const PUFF_GROW_X = 3, PUFF_GROW_Y = 2.4;
/** smoke-puffs: where a puff leaves the vent, in world px. */
const PUFF_OFF_X = 5, PUFF_OFF_Y = 2;
/** smoke-puffs: how far a puff stands off the drift lane, in world px. */
const PUFF_JITTER = 5;
/** smoke-puffs: how far a puff sways on its rise, in world px, and how fast. */
const PUFF_SWAY = 2, PUFF_SWAY_RATE = 4;
/** smoke-puffs: how the puff lies against the drift, in radians. */
const PUFF_ROT = 0.3;
/** smoke-puffs: where the vent pip and the fire pip start to fade. */
const PUFF_VENT_FADE = 0.6, PUFF_FIRE_FADE = 0.5;
/** smoke-puffs: the fire pip's alpha, and how deep its flicker cuts. */
const PUFF_FIRE_ALPHA = 0.8, PUFF_FIRE_REST = 0.6, PUFF_FIRE_BEAT = 0.4;

/** vent-jet, card 07: how long one burst lives, as a share of the life. */
const VENT_BURST_SPAN = 0.5;
/** vent-jet: when the second burst opens, and how strong it is against the
 *  first. */
const VENT_SECOND_AT = 0.45, VENT_SECOND_GAIN = 0.55;
/** vent-jet: how wide the jet opens, in radians. */
const VENT_SPREAD = 0.45;
/** vent-jet: how much of its radius a grain holds at birth, and how far it
 *  swells over the burst. The card writes `(0.5 + u)`. */
const VENT_GRAIN_REST = 0.5;
/** vent-jet: how long the flash at the vent lives, as a share of a burst. */
const VENT_FLASH_SPAN = 0.4;

/** list, card 14: where the heel is full, and where it starts to right
 *  itself, as shares of the life. */
const LIST_HEEL_AT = 0.2, LIST_HOLD_AT = 0.5;
/** list: how far the beam narrows at a full heel, as a share of itself. */
const LIST_SQUASH = 0.2;
/** list: how long the strike flash lives, and its alpha. */
const LIST_FLASH_SPAN = 0.12, LIST_FLASH_ALPHA = 0.8;

/** outline-flicker, card 16: how long the strobe runs, as a share of the
 *  life. The steady line holds the rest. */
const FLICKER_BLINK_AT = 0.4;
/** outline-flicker: the strobe's own depth — what the dark half of a blink
 *  keeps. A strobe's depth is its shape and not a figure. */
const FLICKER_DIM = 0.15;
/** outline-flicker: how far the outline stands off the silhouette, along the
 *  hull axis and across it. */
const FLICKER_SX = 1.12, FLICKER_SY = 1.18;
/** outline-flicker: how long the strike flash lives, and its alpha. */
const FLICKER_FLASH_SPAN = 0.2, FLICKER_FLASH_ALPHA = 0.8;

/** bubble-fire, card 17: how far the flame swells at its brightest, as a
 *  share of its radius at rest. The card writes `5 + 3 * fl`. */
const FLAME_SWELL = 0.6;
/** bubble-fire: what the flame holds between two beats, and how deep a beat
 *  cuts. */
const FLAME_REST = 0.75, FLAME_BEAT = 0.25;
/** bubble-fire: the slow beat, as a share of the fast one. The card writes
 *  `sin(t * 23) * sin(t * 7.3)`. */
const FLAME_SLOW = 7.3 / 23;
/** bubble-fire: the white core's alpha, and where the flame starts to die. */
const FLAME_CORE_ALPHA = 0.8, FLAME_FADE_AT = 0.6;
/** bubble-fire: how many bubbles' lives run over one life of the mark. */
const BUBBLE_RATE = 3;
/** bubble-fire: how long a bubble stays lit fire, as a share of its own
 *  life, and what a cooled one keeps of its alpha. */
const BUBBLE_HOT_AT = 0.35, BUBBLE_COOL = 0.6;
/** bubble-fire: how far a bubble swells as it rises, as a share of itself. */
const BUBBLE_GROW = 0.8;
/** bubble-fire: how far a bubble sways off its lane, in world px, and how
 *  fast, and where it leaves the plate. */
const BUBBLE_SWAY = 2.5, BUBBLE_SWAY_RATE = 8, BUBBLE_OFF_Y = 3;

/** inner-fire, card 18: how often the glow beats over the life. */
const INNER_BEATS = 3;
/** inner-fire: the white core's alpha, and where the glow starts to die. */
const INNER_CORE_ALPHA = 0.7, INNER_FADE_AT = 0.5;
/** inner-fire: how far an ember swells as it rises, as a share of itself.
 *  The card writes `1.6 + 1.5 * age`. */
const EMBER_SWELL = 1.5 / 1.6;
/** inner-fire: how long an ember stays fire, and its alpha. */
const EMBER_HOT_AT = 0.5, EMBER_ALPHA = 0.7;
/** inner-fire: where an ember leaves the plate, in world px. */
const EMBER_OFF_Y = 2;

/** short-circuit: how often the whole silhouette flashes over the life, and
 *  how many of those windows it stands in. The card writes
 *  `floor(p * 9) % 3 === 0`. */
const SHORT_WINDOWS = 9, SHORT_EVERY = 3;
/** short-circuit: the white core's alpha on the strike. */
const SHORT_CORE_ALPHA = 0.95;

/** through-and-through: how long the round takes to cross the hull, as a
 *  share of the life. */
const THROUGH_DELAY = 0.08;
/** through-and-through: how long the entry flash and the exit flash live, as
 *  shares of what is left of the life. */
const THROUGH_IN_SPAN = 0.2, THROUGH_OUT_SPAN = 0.3;
/** through-and-through: how wide the spray opens, in radians, and how long a
 *  grain stays white. */
const THROUGH_SPREAD = 0.6, THROUGH_HOT_AT = 0.3;
/** through-and-through: the exit flash's alpha. */
const THROUGH_OUT_ALPHA = 0.8;

/** The one reused point of this file (R131). */
const S0: Pt = { x: 0, y: 0 };

export const RECIPES: Readonly<Record<string, HitRecipeFn>> = {
  /**
   * Card 04, FRAGMENT FAN (the AUTO look).
   *
   * Plates leave the hull along the line of the shot, tumble, slow down and
   * sink. Each one thins to an edge as it turns, so the fan reads as metal that
   * spins and not as sparks.
   */
  'fragment-fan'(c) {
    const r = c.row;
    const s = onHull(c, S0);
    const base = angOf(c);
    const ink = c.hull ? c.hull.ink : c.ink;
    const reach = out(c.p);
    const fade = r.alpha * tail(c.p, FAN_FADE_AT);
    for (let i = 0; i < r.count; i++) {
      const h0 = hash(c.seed, i * 3 + 1);
      const h1 = hash(c.seed, i * 3 + 2);
      const h2 = hash(c.seed, i * 3 + 3);
      const th = base + (h1 - 0.5) * FAN_SPREAD;
      const d = (r.lengthPx + r.length2Px * h2) * reach;
      const x = s.x + Math.cos(th) * d;
      const y = s.y + Math.sin(th) * d + r.driftPx * c.p * c.p;
      const rot = h0 * TAU + c.p * TAU * (h0 - 0.5) * FAN_SPIN;
      const size = FAN_PLATE_MIN + FAN_PLATE_SPAN * h2;
      const thin = FAN_THIN_MIN + FAN_THIN_SPAN
        * Math.abs(Math.cos(c.p * FAN_THIN_RATE * (h1 + 0.5) + h0 * 6));
      plate(c, x, y, rot, size, thin, ink, fade);
    }
    const flash = 1 - c.p / FAN_FLASH_SPAN;
    if (flash > 0) flare(c, s.x, s.y, r.rPx * flash, COMBAT_WHITE, r.alpha2 * flash);
  },

  /**
   * Card 02, SPARK SHOWER.
   *
   * Sparks fly back toward the shooter in a cone, slow down and sink, each one
   * a short segment that cools from white to fire. The strike point glows fire
   * and cools to a dark pip.
   */
  'spark-shower'(c) {
    const r = c.row;
    const s = onHull(c, S0);
    const base = backOf(c);
    const reach = out(c.p) * SHOWER_DRAG;
    const fade = r.alpha * (1 - c.p);
    const ink = c.p < SHOWER_HOT_AT ? COMBAT_WHITE : FIRE_INK;
    const back = r.width2Px * (1 - c.p);
    for (let i = 0; i < r.count; i++) {
      const h1 = hash(c.seed, i * 2 + 1);
      const h2 = hash(c.seed, i * 2 + 2);
      const th = base + (h1 - 0.5) * SHOWER_SPREAD;
      const d = (r.lengthPx + r.length2Px * h2) * reach;
      const cos = Math.cos(th), sin = Math.sin(th);
      const x = s.x + cos * d;
      const y = s.y + sin * d + r.driftPx * c.p * c.p;
      line(c, x, y, x - cos * back, y - sin * back + SHOWER_DROOP, r.widthPx, ink, fade);
    }
    const flash = 1 - c.p / SHOWER_HOT_AT;
    if (flash > 0) flare(c, s.x, s.y, r.rPx * flash, COMBAT_WHITE, r.alpha2 * flash);
    disc(
      c, s.x, s.y, r.r2Px, c.p < SHOWER_PIP_COOL ? FIRE_INK : DEEP_INK,
      r.alpha * alive(c.p),
    );
  },

  /**
   * Card 06, SMOKE PUFFS.
   *
   * The hull burns inside and vents. Grey puffs leave the strike one after
   * another, swell, drift up and to the right with the current, and thin away.
   * A fire pip flickers at the vent. The neutral ink reads on dark water where
   * soot would not, and the puffs never hide the hull.
   */
  'smoke-puffs'(c) {
    const r = c.row;
    const s = onHull(c, S0);
    for (let i = 0; i < r.count; i++) {
      const age = (c.p - i * PUFF_STEP) / PUFF_SPAN;
      if (age <= 0 || age >= 1) continue;
      const h1 = hash(c.seed, i + 1);
      const x = s.x + PUFF_OFF_X + age * r.lengthPx + (h1 - 0.5) * PUFF_JITTER;
      const y = s.y - PUFF_OFF_Y - age * r.length2Px
        - Math.sin(age * PUFF_SWAY_RATE + i) * PUFF_SWAY;
      fog(
        c, x, y, r.widthPx * (1 + PUFF_GROW_X * age), r.width2Px * (1 + PUFF_GROW_Y * age),
        PUFF_ROT, NEUTRAL_INK, r.alpha * hump(age),
      );
    }
    disc(c, s.x, s.y, r.rPx, DEEP_INK, r.alpha2 * tail(c.p, PUFF_VENT_FADE));
    disc(
      c, s.x, s.y, r.r2Px, FIRE_INK,
      PUFF_FIRE_ALPHA * (PUFF_FIRE_REST + PUFF_FIRE_BEAT * Math.sin(c.t * r.speed))
        * tail(c.p, PUFF_FIRE_FADE),
    );
  },

  /**
   * Card 07, VENT JET.
   *
   * A pressure line breaks. A narrow jet of foam grains shoots out square to
   * the hull on the struck side, a strong burst and then a weaker second one,
   * and the grains spread and rise as the jet dies. A white flash sits at the
   * vent.
   */
  'vent-jet'(c) {
    const r = c.row;
    const s = onHull(c, S0);
    const head = c.hull ? c.hull.head : angOf(c);
    const th = head + sideOf(c) * (Math.PI / 2);
    const cosT = Math.cos(th), sinT = Math.sin(th);
    for (let j = 0; j < r.count2; j++) {
      const u = (c.p - j * VENT_SECOND_AT) / VENT_BURST_SPAN;
      if (u <= 0 || u >= 1) continue;
      const g = j === 0 ? 1 : VENT_SECOND_GAIN;
      const reach = out(u);
      for (let i = 0; i < r.count; i++) {
        const h1 = hash(c.seed, i * 2 + 1 + j * 40);
        const h2 = hash(c.seed, i * 2 + 2 + j * 40);
        const a = th + (h1 - 0.5) * VENT_SPREAD;
        const d = (r.lengthPx + r.length2Px * h2 * g) * reach;
        disc(
          c, s.x + Math.cos(a) * d, s.y + Math.sin(a) * d - r.driftPx * u * u,
          (r.rPx + r.r2Px * h1) * (VENT_GRAIN_REST + u), FOAM_INK, r.alpha * (1 - u) * g,
        );
      }
      flare(
        c, s.x + cosT * r.width2Px, s.y + sinT * r.width2Px, r.widthPx * g,
        COMBAT_WHITE, r.alpha2 * (1 - u / VENT_FLASH_SPAN),
      );
    }
  },

  /**
   * Card 14, LIST.
   *
   * The hull heels. The silhouette turns away from the shot, its beam narrows
   * and its shadow slides out, then it holds and rights itself over the last
   * half.
   *
   * This look OWNS the hull (`OWNS_HULL`): the layer takes the fleet layer's
   * own instance away, which is the map's answer to the `HK.eraseHull` of the
   * card. The card gives the hull up at a heel of nothing; this recipe draws
   * the upright hull instead, because the fleet layer's one is gone for the
   * whole life of the mark and the ship must not go with it.
   */
  list(c) {
    const r = c.row;
    const h = c.hull;
    const flash = 1 - c.p / LIST_FLASH_SPAN;
    if (flash > 0) {
      flare(c, c.at.x, c.at.y, r.rPx * flash, COMBAT_WHITE, LIST_FLASH_ALPHA * flash);
    }
    if (!h || !c.lead) return;
    const side = sideOf(c);
    const k = c.p < LIST_HEEL_AT ? out(c.p / LIST_HEEL_AT)
      : c.p < LIST_HOLD_AT ? 1
        : 1 - inn((c.p - LIST_HOLD_AT) / (1 - LIST_HOLD_AT));
    const nx = -Math.sin(h.head) * side, ny = Math.cos(h.head) * side;
    const squash = 1 - LIST_SQUASH * k;
    hull(
      c, h, h.x + nx * r.widthPx * k, h.y + ny * r.widthPx * k + r.lengthPx * k,
      h.head, 1, squash, DEEP_INK, r.alpha2 * k * alive(c.p),
    );
    hull(
      c, h, h.x + nx * r.width2Px * k, h.y + ny * r.width2Px * k,
      h.head + side * r.speed * k, 1, squash, h.ink, r.alpha * alive(c.p),
      { halo: true },
    );
  },

  /**
   * Card 16, OUTLINE FLICKER.
   *
   * The hull outline strobes. A white hairline round the silhouette blinks, and
   * then a steady line in the faction ink fades over the rest. A hit that reads
   * in grey and at half zoom, because it is the shape of the ship itself.
   *
   * The outline is the kit's stroked master, so its own stroke is the baked
   * one: the card's `lineWidth` has no twin here. The look draws OVER the live
   * hull, the way the card does, and owns nothing.
   */
  'outline-flicker'(c) {
    const r = c.row;
    const h = c.hull;
    const flash = 1 - c.p / FLICKER_FLASH_SPAN;
    if (flash > 0) {
      flare(c, c.at.x, c.at.y, r.rPx * flash, COMBAT_WHITE, FLICKER_FLASH_ALPHA * flash);
    }
    if (!h || !c.lead) return;
    const blink = c.p < FLICKER_BLINK_AT;
    const lit = Math.floor(c.t * r.speed) % 2 === 0;
    const ink = blink ? COMBAT_WHITE : h.ink;
    const a = blink ? (lit ? r.alpha : FLICKER_DIM)
      : r.alpha2 * (1 - (c.p - FLICKER_BLINK_AT) / (1 - FLICKER_BLINK_AT));
    hull(c, h, h.x, h.y, h.head, FLICKER_SX, FLICKER_SY, ink, a, { hollow: true });
  },

  /**
   * Card 17, BUBBLE FIRE.
   *
   * A fire that burns as bubbles. A fire glow flickers at the breach, and small
   * bubbles leave it lit fire, cool to foam as they rise, and go out. The flame
   * dies over the last third. Heat drawn as water, not as a flame.
   */
  'bubble-fire'(c) {
    const r = c.row;
    const s = onHull(c, S0);
    const f = tail(c.p, FLAME_FADE_AT);
    const fl = FLAME_REST
      + FLAME_BEAT * Math.sin(c.t * r.speed) * Math.sin(c.t * r.speed * FLAME_SLOW);
    flare(c, s.x, s.y, r.rPx * (1 + FLAME_SWELL * fl), FIRE_INK, r.alpha2 * f);
    disc(c, s.x, s.y, r.r2Px, COMBAT_WHITE, FLAME_CORE_ALPHA * f);
    const run = c.p * BUBBLE_RATE;
    for (let i = 0; i < r.count; i++) {
      const h0 = hash(c.seed, i * 3 + 1);
      const h1 = hash(c.seed, i * 3 + 2);
      const h2 = hash(c.seed, i * 3 + 3);
      const age = (run + h0) % 1;
      if (age > run) continue;
      const hot = age < BUBBLE_HOT_AT;
      disc(
        c,
        s.x + (h1 - 0.5) * r.lengthPx
          + Math.sin(age * BUBBLE_SWAY_RATE + h2 * 6) * BUBBLE_SWAY * age,
        s.y - BUBBLE_OFF_Y - age * r.length2Px,
        (r.widthPx + r.width2Px * h2) * (1 + age * BUBBLE_GROW),
        hot ? FIRE_INK : FOAM_INK,
        (hot ? r.alpha : BUBBLE_COOL) * hump(age) * f,
      );
    }
  },

  /**
   * Card 18, INNER FIRE.
   *
   * The hull glows from inside. A fire glow beats three times and dies,
   * brightest at the strike, and embers escape the breach and rise.
   *
   * The card masks a fire gradient to the silhouette, and the kit has no clip.
   * THE SILHOUETTE ITSELF carries the glow instead: the hull is washed fire at
   * the card's own alpha and on the card's own beat. The ramp is lost and the
   * hard edge of the plate is kept, because "from inside" is what the card
   * says; an unmasked gradient of the card's radius read as a halo round the
   * ship and not as a fire in it. One mark of a salvo draws the wash, so three
   * rounds cannot wash the hull out. It is a quiet card at 1×, and it is quiet
   * here on purpose.
   */
  'inner-fire'(c) {
    const r = c.row;
    const h = c.hull;
    const s = onHull(c, S0);
    const beat = Math.max(0, Math.sin(c.p * Math.PI * INNER_BEATS)) * clamp01(1 - c.p);
    const f = tail(c.p, INNER_FADE_AT);
    if (h && c.lead) {
      hull(c, h, h.x, h.y, h.head, 1, 1, FIRE_INK, (r.alpha + r.alpha2 * beat) * f);
    }
    flare(c, s.x, s.y, r.rPx, COMBAT_WHITE, INNER_CORE_ALPHA * beat * f);
    const run = c.p * r.speed;
    for (let i = 0; i < r.count; i++) {
      const h1 = hash(c.seed, i * 2 + 1);
      const age = (run + h1) % 1;
      if (age > run) continue;
      flare(
        c, s.x + (h1 - 0.5) * r.lengthPx + age * r.lengthPx,
        s.y - EMBER_OFF_Y - age * r.length2Px,
        r.width2Px * (1 + EMBER_SWELL * age),
        age < EMBER_HOT_AT ? FIRE_INK : PALE_INK,
        EMBER_ALPHA * hump(age) * f,
      );
    }
  },

  /**
   * Card 19, SHORT CIRCUIT.
   *
   * The hit shorts the ship. Jagged accent arcs crawl over the plate from the
   * strike and change on every frame, a white core sits on the point, and the
   * whole silhouette flashes accent three times.
   *
   * The card clips the arcs to the hull; `plateSteps` cuts each run at the
   * hull's own ellipse instead, which is the nearest thing three batches can
   * do. The silhouette flash asks `c.lead`, because a salvo of three rounds
   * would otherwise wash the hull out.
   */
  'short-circuit'(c) {
    const r = c.row;
    const h = c.hull;
    const s = onHull(c, S0);
    const f = 1 - c.p;
    const frame = Math.floor(c.t * r.speed);
    const head = h ? h.head : angOf(c);
    if (h && Math.floor(c.p * SHORT_WINDOWS) % SHORT_EVERY === 0 && c.lead) {
      hull(c, h, h.x, h.y, h.head, 1, 1, ACCENT_INK, r.alpha2 * f);
    }
    for (let i = 0; i < r.count; i++) {
      const th = head + (hash(frame + c.seed, i + 1) - 0.5) * TAU;
      const n = h ? plateSteps(h, s.x, s.y, th, r.lengthPx, r.count2) : r.count2;
      zig(
        c, s.x, s.y, th, n, r.lengthPx, r.length2Px, frame * 5 + i + c.seed,
        r.widthPx, ACCENT_INK, r.alpha * f,
      );
    }
    disc(c, s.x, s.y, r.rPx, COMBAT_WHITE, SHORT_CORE_ALPHA * f);
  },

  /**
   * Card 20, THROUGH AND THROUGH.
   *
   * The round goes through. A flash at the entry, a hairline across the
   * silhouette, and a spray of grains leaves the far side along the line of the
   * shot, slows down and sinks. The exit flashes a moment after the entry.
   */
  'through-and-through'(c) {
    const r = c.row;
    const s = onHull(c, S0);
    const hx = c.hull ? c.hull.x : c.at.x;
    const hy = c.hull ? c.hull.y : c.at.y;
    const ex = hx + c.dirX * r.lengthPx, ey = hy + c.dirY * r.lengthPx;
    const u = clamp01((c.p - THROUGH_DELAY) / (1 - THROUGH_DELAY));
    const inFlash = 1 - c.p / THROUGH_IN_SPAN;
    if (inFlash > 0) flare(c, s.x, s.y, r.rPx, COMBAT_WHITE, r.alpha * inFlash);
    line(c, s.x, s.y, ex, ey, r.widthPx, COMBAT_WHITE, r.alpha2 * (1 - u));
    if (u <= 0) return;
    const outFlash = 1 - u / THROUGH_OUT_SPAN;
    if (outFlash > 0) flare(c, ex, ey, r.r2Px, COMBAT_WHITE, THROUGH_OUT_ALPHA * outFlash);
    const base = angOf(c);
    const reach = out(u);
    const back = r.width2Px * (1 - u);
    const ink = u < THROUGH_HOT_AT ? COMBAT_WHITE : PALE_INK;
    const fade = r.alpha * (1 - u);
    for (let i = 0; i < r.count; i++) {
      const h1 = hash(c.seed, i * 2 + 1);
      const h2 = hash(c.seed, i * 2 + 2);
      const a = base + (h1 - 0.5) * THROUGH_SPREAD;
      const d = (r.rPx + r.length2Px * h2) * reach;
      const cos = Math.cos(a), sin = Math.sin(a);
      const x = ex + cos * d, y = ey + sin * d + r.driftPx * u * u;
      line(c, x, y, x - cos * back, y - sin * back, r.widthPx, ink, fade);
    }
  },
};

/**
 * The cards of round 052 that wait for their own recipe.
 *
 * All ten are built, so nothing waits. An id that stands in neither table fails
 * `combat-recipes-hull.test.ts`.
 */
export const PENDING: readonly string[] = [];

/**
 * Every id whose look draws the victim's hull itself.
 *
 * The layer reads it and takes the fleet layer's hull instance away for the
 * life of the look (`FleetLayer.setSuppressed`). LIST is the one card of the
 * round that moves the whole silhouette, so it is the one that must own it:
 * the card paints the harness hull out first, and a hull left standing under a
 * heeled one reads as two ships. OUTLINE FLICKER strokes the live hull and
 * INNER FIRE and SHORT CIRCUIT draw over it, so none of the three owns it.
 */
export const OWNS_HULL: readonly string[] = ['list'];
